import { useStorage } from '@/src/composables/useStorage';
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  differenceInDays,
  differenceInSeconds,
  format,
  formatDistanceToNow,
  fromUnixTime,
  getUnixTime,
  isSameDay,
  isWithinInterval,
  parse,
  parseISO,
} from 'date-fns';
import { format as formatTz, toZonedTime } from 'date-fns-tz';

type TimeInput = Date | string | number;
type TimeInterval = 'seconds' | 'minutes' | 'hours' | 'days' | 'week' | 'month';
type CompareBy = 'day' | 'exact';

type TimeCalculationsInterface = {
  getTimezone(): string;
  getDaysFromDate(fromDate: TimeInput, toDate: TimeInput): number;
  getSecondsFromDate(fromDate: TimeInput, toDate: TimeInput): number;
  getCurrentTime(): Date;
  getCurrentUnixTime(): number;
  getTimeFromNow(time: TimeInput): string;
  formatTime(
    time: TimeInput,
    relative?: boolean,
    showSeconds?: boolean,
    showMinutes?: boolean,
    showHours?: boolean,
    dataTimezone?: string,
    outputTimezone?: string
  ): string;
  formatDate(date: TimeInput): string;
  formatUnixTime(time: TimeInput, formatString: string): string;
  datesEqual(date1: TimeInput, date2: TimeInput, compareBy?: CompareBy): boolean;
  isTimeBetween(dateCheck: TimeInput, dateFrom: TimeInput, dateTo: TimeInput): boolean;
  parseDate(time: TimeInput, timezone?: string): Date;
  rawFormat(time: TimeInput, formatString: string): string;
  rawFormatBeginningOf(time: TimeInput, beginningOf: string, formatString: string): string;
  rawAddTime(timeObject: TimeInput, numberOfIntervals: number, interval: TimeInterval): Date;
  rawSetTimezone(timeObject: TimeInput, timezone: string): Date;
  toTimeString(seconds: string, noHours: boolean): string;
};

const { getStorageValue, setStorageValue } = useStorage();

const getTimezone = (): string => {
  let timezone = getStorageValue('_tz');
  if (!timezone) {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setStorageValue('_tz', timezone);
  }
  return timezone;
};

const getDaysFromDate = (fromDate: TimeInput, toDate: TimeInput): number => {
  return differenceInDays(parseDate(toDate), parseDate(fromDate));
};

const getSecondsFromDate = (fromDate: TimeInput, toDate: TimeInput): number => {
  return differenceInSeconds(parseDate(toDate), parseDate(fromDate));
};

const getCurrentTime = (): Date => {
  return new Date();
};

const getCurrentUnixTime = (): number => {
  return getUnixTime(new Date());
};

const getTimeFromNow = (time: TimeInput): string => {
  const timezone = getTimezone();
  const zonedTime = toZonedTime(parseDate(time), timezone);
  return formatDistanceToNow(zonedTime, { addSuffix: true });
};

const formatTime = (
  time: TimeInput,
  relative = true,
  showSeconds = false,
  showMinutes = true,
  showHours = true,
  dataTimezone = 'UTC',
  outputTimezone = undefined
): string => {
  if (!time) {
    return '';
  }
  const now = new Date();
  const zonedTime = toZonedTime(parseDate(time, dataTimezone), outputTimezone || getTimezone());
  if (Math.abs(differenceInDays(zonedTime, now)) > 1 || !relative) {
    let formatString = 'dd-MM-yyyy';
    if (showHours) {
      formatString += ', HH';
      if (showMinutes) {
        formatString += ':mm';
        if (showSeconds) {
          formatString += ':ss';
        }
      }
    }
    return formatTz(zonedTime, formatString);
  } else if (Math.abs(differenceInSeconds(now, zonedTime)) < 60) {
    return 'just now';
  } else {
    return formatDistanceToNow(zonedTime, { addSuffix: true });
  }
};

const formatDate = (date: TimeInput): string => {
  return formatTime(date, false, false, false, false);
};

const datesEqual = (date1: TimeInput, date2: TimeInput, compareBy: CompareBy = 'day'): boolean => {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  return compareBy === 'day' ? isSameDay(d1, d2) : d1.getTime() === d2.getTime();
};

const isTimeBetween = (dateCheck: TimeInput, dateFrom: TimeInput, dateTo: TimeInput): boolean => {
  return isWithinInterval(parseDate(dateCheck), {
    start: parseDate(dateFrom),
    end: parseDate(dateTo),
  });
};

const parseDate = (time: TimeInput, timezone = 'UTC'): Date => {
  let utcDate: Date;

  if (typeof time === 'number') {
    // Convert Unix timestamp to UTC Date
    utcDate = fromUnixTime(time);
  } else if (typeof time === 'string') {
    // Check if the string already has a UTC indicator
    if (time.endsWith('Z')) {
      // Already marked as UTC, parse directly
      utcDate = parseISO(time);
    } else {
      // No UTC indicator - convert to ISO format if needed
      if (time.includes(' ')) {
        // Convert "YYYY-MM-DD HH:mm:ss" to ISO format
        time = time.replace(' ', 'T');
      }
      // Append 'Z' to treat as UTC
      utcDate = parseISO(time + 'Z');
    }
  } else {
    // Already a Date object - clone it to avoid modifying the original
    utcDate = new Date(time.getTime());
  }

  // Convert from UTC to the specified timezone
  // If timezone is 'UTC', this effectively does nothing
  return timezone === 'UTC' ? utcDate : toZonedTime(utcDate, timezone);
};

const rawFormat = (time: TimeInput, formatString: string): string => {
  return format(parseDate(time), formatString);
};

const formatUnixTime = (time: TimeInput, formatString: string): string => {
  return format(fromUnixTime(time as number), formatString);
};

const rawFormatBeginningOf = (time: TimeInput, beginningOf: string, formatString: string): string => {
  const date = parseDate(time);
  // Set to start of period
  date.setHours(0, 0, 0, 0);
  return format(date, formatString);
};

const rawAddTime = (timeObject: TimeInput, numberOfIntervals: number, interval: TimeInterval): Date => {
  const date = parseDate(timeObject);
  switch (interval) {
    case 'seconds':
      return addSeconds(date, numberOfIntervals);
    case 'minutes':
      return addMinutes(date, numberOfIntervals);
    case 'hours':
      return addHours(date, numberOfIntervals);
    case 'days':
      return addDays(date, numberOfIntervals);
    case 'week':
      return addWeeks(date, numberOfIntervals);
    case 'month':
      return addMonths(date, numberOfIntervals);
    default:
      return date;
  }
};

const rawSetTimezone = (timeObject: TimeInput, timezone: string): Date => {
  return toZonedTime(timeObject, timezone);
};

/**
 * Converts seconds to HH:MM:SS or MM:SS format
 * @param seconds The number of seconds to convert
 * @param noHours If true, returns MM:SS format instead of HH:MM:SS
 * @returns Formatted time string
 */
const toTimeString = (seconds: string, noHours: boolean = false): string => {
  if (!seconds) {
    return '00:00';
  }
  const secondsNumber = parseInt(seconds, 10);
  const sec_num = Math.floor(secondsNumber); // Ensure we have a whole number
  const hours = Math.floor(sec_num / 3600);
  const minutes = Math.floor((sec_num - hours * 3600) / 60);
  const secs = sec_num - hours * 3600 - minutes * 60;

  // Pad with zeros using string literals
  const hoursStr = hours < 10 ? `0${hours}` : `${hours}`;
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  const secondsStr = secs < 10 ? `0${secs}` : `${secs}`;

  return noHours ? `${minutesStr}:${secondsStr}` : `${hoursStr}:${minutesStr}:${secondsStr}`;
};

const rawDateObject = (time: TimeInput, format = '', timezone = 'UTC'): Date => {
  // UTC is the timezone configured in Laravel settings.
  if (typeof time === 'number') {
    return toZonedTime(fromUnixTime(time), timezone);
  } else {
    if (typeof time === 'string' && !format) {
      return toZonedTime(new Date(time), timezone);
    }
    if (format) {
      return toZonedTime(parse(time as string, format, new Date()), timezone);
    }
    return toZonedTime(new Date(time), timezone);
  }
};

const TimeCalculations = {
  datesEqual,
  formatDate,
  formatTime,
  formatUnixTime,
  getCurrentTime,
  getCurrentUnixTime,
  getDaysFromDate,
  getSecondsFromDate,
  getTimeFromNow,
  getTimezone,
  isTimeBetween,
  parseDate,
  rawAddTime,
  rawFormat,
  rawFormatBeginningOf,
  rawSetTimezone,
  toTimeString,
  rawDateObject,
};

export { TimeCalculations };
export type { CompareBy, TimeCalculationsInterface, TimeInput, TimeInterval };
